'use client'

import { JSX, useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import useSWRInfinite from '@hooks/client/useSWRInfinite'
import { SWRConfig } from 'swr'

import type { BlogPost, Country, PaginatedResponse } from '@types'

import { queryString, seoUrlPathEncode } from '@lib/helpers/client'

import styles from '../../v1.module.scss'

type V1BlogsInfiniteProps = {
  locale: Country
  initialPosts: PaginatedResponse<BlogPost[]>
  featuredId?: string
  initialLimit?: number
}

export default function V1BlogsInfinite({
  locale,
  initialPosts,
  featuredId,
  initialLimit = 6,
}: V1BlogsInfiniteProps): JSX.Element {
  const query = useSearchParams()

  const getApiKey = (index: number, prev?: PaginatedResponse<BlogPost[]>): string | null => {
    if (initialPosts.totalPages <= 1) return null
    if (prev && index + 2 > prev.totalPages) return null
    return `/blogs/posts?${queryString({
      page: index + 2,
      query: query?.get('query'),
      category: query?.get('category'),
    })}`
  }

  const filteredInitial = useMemo(() => {
    const content = featuredId ? initialPosts.content.filter((post) => post.id !== featuredId) : initialPosts.content
    return { ...initialPosts, content }
  }, [featuredId, initialPosts])

  const initialSlice = filteredInitial.content.slice(0, initialLimit)
  const overflowSlice = filteredInitial.content.slice(initialLimit)
  const excludedIds = useMemo(() => new Set(initialSlice.map((post) => post.id)), [initialSlice])

  return (
    <SWRConfig value={{ fallback: {} }}>
      <div className={styles.blogsPostsGrid}>
        {initialSlice.map((post) => (
          <BlogCard key={`${post.id}-${post.date}`} locale={locale} post={post} />
        ))}
      </div>
      <V1BlogsInfiniteInner
        locale={locale}
        featuredId={featuredId}
        getApiKey={getApiKey}
        excludedIds={excludedIds}
        initialTotalPages={initialPosts.totalPages}
        overflowPosts={overflowSlice}
      />
    </SWRConfig>
  )
}

function V1BlogsInfiniteInner({
  locale,
  featuredId,
  getApiKey,
  excludedIds,
  initialTotalPages,
  overflowPosts,
}: {
  locale: Country
  featuredId?: string
  getApiKey: (index: number, prev?: PaginatedResponse<BlogPost[]>) => string | null
  excludedIds: Set<string>
  initialTotalPages: number
  overflowPosts: BlogPost[]
}): JSX.Element {
  const { data, setSize, isValidating } = useSWRInfinite<PaginatedResponse<BlogPost[]>>(getApiKey, undefined, {
    revalidateFirstPage: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateOnMount: false,
    revalidateIfStale: false,
  })

  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const [hasTriggered, setHasTriggered] = useState(initialTotalPages <= 1)
  const lastPage = data?.[data.length - 1]
  const hasMore = lastPage ? data.length + 1 < lastPage.totalPages : initialTotalPages > 1

  useEffect(() => {
    const node = sentinelRef.current
    if (!node) return

    // KaiOS 2.5 is out of support and does not run infinite-scroll pagination anyway.
    // eslint-disable-next-line compat/compat
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && !isValidating && hasMore) {
          setHasTriggered(true)
          setSize((current) => current + 1)
        }
      },
      { rootMargin: '200px' },
    )

    observer.observe(node)
    return (): void => observer.disconnect()
  }, [hasMore, isValidating, setSize])

  const pages = Array.isArray(data) ? data : data ? [data] : []
  const allPosts = pages.flatMap((page) => page.content || [])
  const posts = allPosts.filter((post) => {
    if (featuredId && post.id === featuredId) return false
    if (excludedIds.has(post.id)) return false
    return true
  })
  const combinedPosts = hasTriggered ? [...overflowPosts, ...posts] : []

  return (
    <>
      {combinedPosts.length > 0 && (
        <div className={styles.blogsPostsGrid} data-pw="blog-grid">
          {combinedPosts.map((post) => (
            <BlogCard key={`${post.id}-${post.date}`} locale={locale} post={post} />
          ))}
        </div>
      )}
      <div ref={sentinelRef} />
      {isValidating && <div className={styles.blogsLoadMore}>Loading more...</div>}
      {!hasMore && <div className={styles.blogsLoadMore}>No more posts.</div>}
    </>
  )
}

function BlogCard({ locale, post }: { locale: Country; post: BlogPost }): JSX.Element {
  const postUrl = `/${locale}/blogs/${seoUrlPathEncode(post.title, 'blog')}-${post.id}`
  const category = post.categories?.[0]?.name
  const formattedDate = new Intl.DateTimeFormat('en-US', { month: 'short', day: '2-digit', year: 'numeric' }).format(
    new Date(post.date || Date.now()),
  )

  return (
    <Link href={postUrl} className={styles.blogsPostCard} data-pw="post-card">
      <div className={styles.blogsPostMeta}>
        {category && <span className={styles.blogsPostCategory}>{category}</span>}
        <span className={styles.blogsPostDate}>{formattedDate}</span>
      </div>
      <h3 className={styles.blogsPostTitle}>{post.title}</h3>
      <p className={styles.blogsPostExcerpt}>{post.excerpt || post.subTitle}</p>
      <div className={styles.blogsPostFooter}>
        <div className={styles.blogsPostStats}>
          <span className={styles.blogsPostStat}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={styles.blogsPostStatIcon}
            >
              <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z"></path>
            </svg>
            {post.numComments ?? 0}
          </span>
          <span className={styles.blogsPostStat}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={styles.blogsPostStatIconAccent}
            >
              <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"></path>
            </svg>
            {post.likesCount ?? 0}
          </span>
        </div>
        <span className={styles.blogsPostLink}>
          Read
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={styles.blogsPostLinkIcon}
          >
            <path d="M5 12h14"></path>
            <path d="m12 5 7 7-7 7"></path>
          </svg>
        </span>
      </div>
    </Link>
  )
}
